<template>
	<div>
		<b-row class="mt-3">
			<b-col>
				<b-link
					class="float-right ml-3"
					@click.prevent="deleteCustomer"
					title="Delete this customer"
				>
					<b-icon
						font-scale="1.5"
						icon="trash"
						variant="danger"
					></b-icon>
				</b-link>

				<save-or-edit-buttons
					:editable="nameEditable"
					section="nameEditable"
					@save="saveSection"
					@edit="editSection"
				></save-or-edit-buttons>
			</b-col>
		</b-row>

		<b-row class="mt-3" v-if="nameEditable">
			<b-col>
				<b-form-group breakpoint="md" horizontal>
					<b-form-input
						id="name"
						placeholder="Customer Name"
						v-model="customer.Name"
						maxlength="60"
						required
					/>
				</b-form-group>
			</b-col>

			<b-col>
				<b-form-group breakpoint="md" horizontal>
					<b-form-input
						id="reference"
						placeholder="Sage Reference"
						v-model="customer.Reference"
						maxlength="60"
					/>
				</b-form-group>
			</b-col>

			<b-col>
				<b-form-group
					breakpoint="md"
					label-cols-sm="4"
					label="PO required?"
				>
					<b-form-radio-group
						id="porequired"
						v-model="customer.PORequired"
						buttons
						button-variant="outline-secondary"
					>
						<b-form-radio value="true">Yes</b-form-radio>
						<b-form-radio value="false">No</b-form-radio>
					</b-form-radio-group>
				</b-form-group>
			</b-col>
		</b-row>

		<b-row class="mt-3 mb-3" v-else>
			<b-col cols="12">
				<h1>{{ customer.Name }}</h1>
			</b-col>

			<b-col cols="12">
				<small>Sage reference: {{ customer.Reference }}</small>
			</b-col>
		</b-row>

		<b-tabs content-class="mt-3">
			<b-tab title="Clocks" active>
				<b-row>
					<b-col cols="10">
						<h4>Clocks</h4>
					</b-col>
					<b-col cols="2">
						<b-link
							class="float-right mb-3"
							v-b-modal.sharedModal
							title="Add a clock to this customer"
							@click.prevent="
								modalContent = 'clock';
								modalTitle = 'Select Clock';
							"
						>
							<b-icon
								font-scale="1.5"
								icon="plus-square"
							></b-icon>
						</b-link>
					</b-col>
				</b-row>

				<b-form-fieldset>
					<b-table-simple bordered>
						<b-thead head-variant="light">
							<tr>
								<th>Name</th>
								<th>Location</th>
								<th style="width: 12.5%"></th>
							</tr>
						</b-thead>

						<b-tbody>
							<tr
								v-for="(clock, index) in customer.Clocks"
								:key="index + 10"
							>
								<td>
									<b-link
										:to="{
											name: 'clockupdate',
											params: { id: clock.Id },
										}"
										>{{ clock.Name }}</b-link
									>
								</td>

								<td>{{ clock.Address.Town }}</td>

								<td class="text-right">
									<b-link
										title="Remove clock from this customer"
										@click.prevent="
											removeFromArray(
												'Clocks',
												index,
												true
											)
										"
									>
										<b-icon
											font-scale="1.5"
											icon="dash-square"
											variant="danger"
										></b-icon>
									</b-link>
								</td>
							</tr>
						</b-tbody>
					</b-table-simple>
				</b-form-fieldset>

				<b-form-fieldset>
					<b-row class="mt-3">
						<b-col cols="10">
							<h4>Service Contracts</h4>
						</b-col>
						<b-col cols="2">
							<b-link
								class="float-right mb-3"
								v-b-modal.sharedModal
								title="Add a service contract to this customer"
								@click.prevent="
									modalContent = 'contract';
									modalTitle = 'Add Service Contract';
								"
							>
								<b-icon
									font-scale="1.5"
									icon="plus-square"
								></b-icon>
							</b-link>
						</b-col>
					</b-row>

					<b-table-simple bordered>
						<b-thead head-variant="light">
							<tr>
								<th>Reference</th>
								<th class="col-md-6">Clocks</th>
								<th>Cost</th>
								<th>Frequency</th>
								<th>Next Service</th>
								<th>Status</th>
							</tr>
						</b-thead>

						<b-tbody>
							<tr
								v-for="(
									contract, index
								) in customer.ServiceContracts"
								:key="index + 10"
							>
								<td>
									<b-link
										:to="{
											name: 'contractupdate',
											params: { id: contract.Id },
										}"
										>{{ contract.Reference }}</b-link
									>
								</td>

								<td>
									<span
										v-for="(
											clock, index
										) in contract.Clocks"
										:key="clock.Id"
									>
										{{ clock.Name }}
										<span
											v-if="
												index !=
												contract.Clocks.length - 1
											"
											>,</span
										>
									</span>
								</td>
								<td>{{ contract.InvoiceCode }}</td>
								<td>{{ contract.Frequency }}</td>

								<td>{{ formatDate(contract.FirstService) }}</td>
								<td align="center">
									<b-icon
										font-scale="1.5"
										:icon="serviceClass(contract.Status)"
									></b-icon>
								</td>
							</tr>
						</b-tbody>
					</b-table-simple>
				</b-form-fieldset>
			</b-tab>

			<b-tab title="Contacts">
				<b-form-fieldset>
					<b-row class="mt-3">
						<b-col>
							<h4>Invoice contact</h4>
						</b-col>

						<b-col>
							<b-link
								class="float-right ml-3"
								v-b-modal.sharedModal
								title="Set the invoice contact for this customer"
								@click.prevent="
									modalContent = 'invoice-contact';
									modalTitle = 'Set Invoice Contact';
								"
							>
								<b-icon
									font-scale="1.6"
									icon="pencil-square"
								></b-icon>
							</b-link>

							<b-link
								v-if="customer.InvoiceContactId != null"
								class="float-right ml-3"
								title="Remove invoice contact"
								@click.prevent="removeInvoiceContact"
							>
								<b-icon
									font-scale="1.5"
									icon="dash-square"
									variant="danger"
								></b-icon>
							</b-link>
						</b-col>

						<b-col cols="12">
							<b-card bg-variant="light">
								<b-card-text
									class="d-flex justify-content-start"
								>
									<b-link
										class="p-2"
										href="#"
										@click.prevent="
											editContact(
												customer.InvoiceContact.Id,
												null
											)
										"
										>{{
											customer.InvoiceContact.Name
										}}</b-link
									>
									<span class="p-2">{{
										customer.InvoiceContact.Telephone
									}}</span>
									<span class="p-2">{{
										customer.InvoiceContact.EmailAddress
									}}</span>
								</b-card-text>
							</b-card>
						</b-col>
					</b-row>
				</b-form-fieldset>

				<b-form-fieldset>
					<b-row class="mt-3">
						<b-col cols="10">
							<h4>Admin contacts</h4>
						</b-col>
						<b-col cols="2">
							<b-link
								class="float-right mb-3"
								v-b-modal.sharedModal
								title="Add a contact"
								@click.prevent="
									modalContent = 'add-contact';
									modalTitle = 'Select Contact';
								"
							>
								<b-icon
									font-scale="1.5"
									icon="plus-square"
								></b-icon>
							</b-link>
						</b-col>
					</b-row>

					<b-table-simple bordered>
						<b-thead head-variant="light">
							<tr>
								<th>Name</th>
								<th>Duties</th>
								<th>Note</th>
								<th>Phone</th>
								<th style="width: 15%"></th>
							</tr>
						</b-thead>

						<draggable
							v-model="customer.Contacts"
							tag="b-tbody"
							@end="setContactOrder"
						>
							<tr
								v-for="(contact, index) in customer.Contacts"
								:key="index + 10"
							>
								<td>
									<b-link
										href="#"
										@click.prevent="
											editContact(contact.Id, index)
										"
										>{{ contact.Name }}</b-link
									>
								</td>

								<td v-if="contact.Editable">
									<v-select
										multiple
										v-model="contact.Duties"
										label="Name"
										:options="contactDuties"
									></v-select>
								</td>
								<td v-else>
									<span
										v-for="(value, index) in contact.Duties"
										:key="value.Id"
									>
										{{ value.Name }}
										<span
											v-if="
												index !=
												contact.Duties.length - 1
											"
											>,</span
										>
									</span>
								</td>
								<td v-if="contact.Editable">
									<b-form-input
										v-model="contact.Note"
										type="text"
									/>
								</td>
								<td v-else>{{ contact.Note }}</td>

								<td>
									{{
										contact.Telephone
											? contact.Telephone +
											  (contact.Mobile
													? " | " + contact.Mobile
													: "")
											: contact.Mobile
									}}
								</td>

								<td>
									<b-link
										v-if="!contact.Editable"
										class="ml-3 float-right"
										title="Delete this contact"
										@click.prevent="
											removeFromArrayAndDelete(
												'Contacts',
												index,
												contact.Id,
												true
											)
										"
									>
										<b-icon
											font-scale="1.5"
											icon="trash"
											variant="danger"
										></b-icon>
									</b-link>
									<b-link
										v-if="!contact.Editable"
										class="ml-3 float-right"
										title="Remove this contact"
										@click.prevent="
											removeFromArray(
												'Contacts',
												index,
												true
											)
										"
									>
										<b-icon
											font-scale="1.5"
											icon="dash-square"
											variant="danger"
										></b-icon>
									</b-link>

									
									<save-or-edit-buttons-row
										:section="contact"
										@save="updateCustomer"
									></save-or-edit-buttons-row>
								</td>
							</tr>
						</draggable>
					</b-table-simple>
				</b-form-fieldset>
			</b-tab>

			<b-tab title="Jobs"> </b-tab>
		</b-tabs>

		<b-modal
			id="sharedModal"
			ref="sharedModal"
			:title="modalTitle"
			hide-footer
		>
			<set-clock v-if="modalContent == 'clock'" @add="addClock" />

			<add-contact
				v-if="modalContent == 'add-contact'"
				@addContact="addContact"
			/>

			<add-contact
				v-if="modalContent == 'invoice-contact'"
				@addContact="setInvoiceContact"
			/>

			<edit-contact
				v-if="modalContent == 'edit-contact'"
				@updateContact="updateContact"
				:editing="editing"
			/>

			<add-contract
				v-if="modalContent == 'contract'"
				@addContract="addContract"
				:customer="customer"
			/>
		</b-modal>

		<confirm ref="confirm"></confirm>
	</div>
</template>

<script>
import ContactService from "@/api-services/contact.service";

import SetClock from "@/components/partials/set-clock";
import AddContact from "@/components/partials/add-contact-to-parent";
import AddContract from "@/components/partials/add-contract-to-parent";
import EditContact from "@/components/partials/edit-contact";
import SaveOrEditButtons from "@/components/partials/save-or-edit-buttons";
import SaveOrEditButtonsRow from "@/components/partials/save-or-edit-buttons-row";
import Confirm from "@/components/partials/confirm";

import Draggable from "vuedraggable";

import VSelect from "vue-select";
import "vue-select/dist/vue-select.css";

import { dateFuncs } from "@/globs.js";

export default {
	name: "CustomerForm",

	props: ["customer"],

	components: {
		SetClock,
		AddContact,
		AddContract,
		EditContact,
		Draggable,
		SaveOrEditButtons,
		SaveOrEditButtonsRow,
		VSelect,
		Confirm,
	},

	data: function () {
		return {
			contactDuties: [
				{ Id: 1, Name: "Primary Contact" },
				{ Id: 3, Name: "Emergency" },
				{ Id: 5, Name: "Secondary" },
				{ Id: 6, Name: "Owner" },
				{ Id: 12, Name: "Conservation Inspector" },
			],

			editing: {
				id: null,
				index: null,
			},

			modalContent: null,
			modalTitle: "Select",

			nameEditable: false,
		};
	},

	methods: {
		editSection(section) {
			this[section] = true;
		},

		saveSection(section) {
			this[section] = false;
			this.updateCustomer();
		},

		removeFromArray(section, index, hold) {
			if (hold) {
				this.$refs.confirm
					.open(
						"Confirm action",
						"Are you sure you want to remove this item from " +
							section
					)
					.then((confirm) => {
						if (confirm) {
							this.removeFromArray(section, index);
						}
					});
			} else {
				this.$delete(this.customer[section], index);
				this.updateCustomer();
			}
		},

		removeFromArrayAndDelete(section, index, id, hold) {
			if (hold) {
				this.$refs.confirm
					.open(
						"Confirm action",
						"Are you sure you want to delete this contact completely?"
					)
					.then((confirm) => {
						if (confirm) {
							this.removeFromArrayAndDelete(section, index, id);
						}
					});
			} else {
				ContactService.delete(id)
					.then(() => {
						this.$delete(this.customer[section], index);
						this.updateCustomer();
					})
					.catch((error) => {
						let modal_options = {
							text: "light",
							background: "danger",
						};

						this.$refs.confirm.open(
							"Error",
							"Can't delete because the contact is linked to another clock or customer, try removing instead.",
							true,
							modal_options
						);
					});
			}
		},

		addClock(clock) {
			this.customer.Clocks.push({
				Id: clock.Id,
				Name: clock.Name,
				Address: clock.Address,
			});

			this.updateCustomer();

			this.$nextTick(() => {
				this.modalContent = null;
				this.$refs.sharedModal.hide();
			});
		},

		addContract(contractFromForm) {
			// make sure we can read the date
			let contractSqlDate = contractFromForm.FirstService;

			contractFromForm.FirstService = dateFuncs.frontEndDateFormat(
				contractSqlDate
			);

			this.customer.ServiceContracts.push(contractFromForm);

			// Hide the modal
			this.$nextTick(() => {
				this.updateCustomer(); //this.updateCustomer();
				this.modalContent = null;
				this.$refs.sharedModal.hide();
			});
		},

		addContact(contactFromForm) {
			var valObj = this.customer.Contacts.filter(function (elem) {
				if (elem.Id === contactFromForm.Id) return elem;
			});

			if (valObj.length > 0) {
				this.$refs.confirm.open(
					"Error",
					"Contact already assigned",
					true
				);
			} else {
				this.customer.Contacts.push(contactFromForm);
				this.updateCustomer();
			}
			// Hide the modal
			this.$nextTick(() => {
				this.modalContent = null;
				this.$refs.sharedModal.hide();
			});
		},

		editContact(contactId, index) {
			this.editing.id = contactId;
			this.editing.index = index;

			this.$nextTick(() => {
				this.modalContent = "edit-contact";
				this.modalTitle = "Edit Contact";
				this.$refs.sharedModal.show();
			});
		},

		updateContact(contactFromForm) {
			// our newly updated contact - we need to set the basics to visually update

			let foundContact = this.customer.Contacts.find(
				(contact) => contact.Id === this.editing.id
			);

			foundContact.Name = contactFromForm.Name;
			foundContact.Email = contactFromForm.Email;
			foundContact.Telephone = contactFromForm.Telephone;
			foundContact.Mobile = contactFromForm.Mobile;

			// is this also the invoice contact?
			if (contactFromForm.Id == this.customer.InvoiceContactId) {
				this.customer.InvoiceContact.Name = contactFromForm.Name;
				this.customer.InvoiceContact.Telephone =
					contactFromForm.Telephone;
				this.customer.InvoiceContact.Mobile = contactFromForm.Mobile;
				this.customer.InvoiceContact.Email = contactFromForm.Email;
			}

			this.editing = {
				id: null,
				index: null,
			};

			this.$nextTick(() => {
				this.modalContent = null;
				this.$refs.sharedModal.hide();
			});
		},

		setContactOrder() {
			this.customer.Contacts.forEach((contact, i) => {
				contact.Order = i;
			});
		},

		setInvoiceContact(contactFromForm) {

			console.log(contactFromForm);

			
			this.customer.InvoiceContactId = contactFromForm.Id;
			this.customer.InvoiceContact = contactFromForm;

			this.$nextTick(() => {
				this.updateCustomer();
				this.modalContent = null;
				this.$refs.sharedModal.hide();
			});
		},

		removeInvoiceContact() {
			this.$refs.confirm
				.open(
					"Confirm action",
					"Are you sure you want remove the invoice contact?"
				)
				.then((confirm) => {
					this.customer.InvoiceContactId = null;
					this.customer.InvoiceContact = {};

					this.$nextTick(() => {
						this.updateCustomer();
						this.modalContent = null;
						this.$refs.sharedModal.hide();
					});
				});
		},

		serviceClass(status) {
			let tempClass = "x-circle";

			switch (status) {
				case "Active":
					tempClass = "check-circle";
					break;
				case "Paused":
					tempClass = "pause";
					break;
				case "Ended":
					tempClass = "dash-circle";
					break;
			}

			return tempClass;
		},

		formatDate(date) {
			return dateFuncs.humanDateFormat(date);
		},

		notEmpty(obj) {
			console.log("calling actual function");
			return dateFuncs.notEmpty(obj);
		},

		updateCustomer() {
			this.$emit("update");
		},

		deleteCustomer() {
			this.$emit("delete");
		},
	},
};
</script>



// WEBPACK FOOTER //
// src/components/customer/customer-form.vue